import Close from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Skeleton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Loading, Button, CardHeader, IconButton, Tooltip } from 'components'
import {
  useApproveReserveOrderLink,
  useLoadOrder,
  useLoadOrderDirtRoadQuizResponses,
  useLoadOrderProposal,
  useLoadOrderReviewReasons
} from 'modules/orders/hooks'
import type {
  ItemsWithDiscount,
  OrderLineItem,
  OrderReviewReasonsData
} from 'modules/orders/hooks/useLoadOrderReviewReasons'
import { useState } from 'react'
import { formatCurrency } from 'utils'
import { useApproveReserveOrderLinkModalStore } from './approveReserveOrderLinkModalStore'
import { RejectReserveOrderLinkModal } from './RejectReserveOrderLinkModal'

const tableCellSx: SxProps<Theme> = { py: 1, px: 1.25, fontSize: '0.875rem' }
const tableHeadCellSx: SxProps<Theme> = {
  py: 1,
  px: 1.25,
  fontSize: '0.875rem',
  fontWeight: 'bold',
  color: 'text.secondary',
  whiteSpace: 'nowrap'
}

const MessageReason = ({ reason }: { reason?: string }) => {
  if (!reason) return null

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="body2">{reason}</Typography>
      </Box>
    </Grid>
  )
}

const DiscountStat = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ minWidth: 0, flex: '1 1 45%' }}>
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {label}
    </Typography>
    <Typography variant="caption" fontWeight={600} noWrap>
      {value}
    </Typography>
  </Box>
)

const ItemsDiscountReason = ({ reason }: { reason?: ItemsWithDiscount[] }) => {
  if (!reason?.length) return null

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
        Desconto aplicado maior que o permitido
      </Typography>
      <Stack
        spacing={0.75}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {reason.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              px: 1,
              py: 0.75,
              bgcolor: 'background.paper',
              borderTop: index > 0 ? '1px solid' : 'none',
              borderColor: 'divider'
            }}
          >
            <Tooltip title={`${item.productId} - ${item.name}`}>
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                display="block"
              >
                {item.productId} — {item.name}
              </Typography>
            </Tooltip>
            {item?.configurationDescription && (
              <Tooltip title={item.configurationDescription} maxWidth={500}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                  sx={{ mt: 0.25 }}
                >
                  {item.configurationDescription}
                </Typography>
              </Tooltip>
            )}
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
              <DiscountStat
                label="Desc. máx."
                value={`${item.maximumDiscount}%`}
              />
              <DiscountStat label="Desc. apl." value={`${item.discount}%`} />
              <DiscountStat
                label="Pr. formado"
                value={formatCurrency(item.defaultPrice)}
              />
              <DiscountStat
                label="Pr. aplicado"
                value={formatCurrency(item.salePrice)}
              />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Grid>
  )
}

const DirtRoadReason = ({ reason }: { reason?: string | boolean }) => {
  const proposalId = useApproveReserveOrderLinkModalStore.useProposalId()

  const {
    data: responses,
    isLoading: loading,
    isFetching
  } = useLoadOrderDirtRoadQuizResponses({
    proposalId
  })

  const isLoading = loading || isFetching

  if (!reason) return null

  const quests = [
    {
      label: 'Km estrada de chão',
      value: isLoading ? null : `${responses?.data?.quest1 ?? '-'} km`
    },
    {
      label: 'Carreta entra e retorna',
      value: isLoading ? null : responses?.data?.quest2 ? 'SIM' : 'NÃO'
    },
    {
      label: 'Caminhão na chuva',
      value: isLoading ? null : responses?.data?.quest3 ? 'SIM' : 'NÃO'
    },
    {
      label: 'Localização anexada',
      value: isLoading ? null : responses?.data?.quest4 ? 'SIM' : 'NÃO'
    }
  ]

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
        Estrada de chão
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          gap: 0.75,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 0.75,
          bgcolor: 'background.paper'
        }}
      >
        {quests.map((quest) => (
          <Box key={quest.label} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              lineHeight={1.2}
            >
              {quest.label}
            </Typography>
            {quest.value === null ? (
              <Skeleton width={48} height={18} sx={{ mt: 0.25 }} />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {quest.value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Grid>
  )
}

const reviewReasonKeys = [
  'payment',
  'delivery',
  'minimalOrder',
  'minimalFreight',
  'itemsWithDiscount',
  'dirtRoadQuiz'
] as const

type ReviewReasonKey = typeof reviewReasonKeys[number]

const ReviewReasonBlock = ({
  reasonKey,
  reviewData
}: {
  reasonKey: ReviewReasonKey
  reviewData?: OrderReviewReasonsData
}) => {
  switch (reasonKey) {
    case 'payment':
      return <MessageReason reason={reviewData?.payment} />
    case 'delivery':
      return <MessageReason reason={reviewData?.delivery} />
    case 'minimalOrder':
      return <MessageReason reason={reviewData?.minimalOrder} />
    case 'minimalFreight':
      return <MessageReason reason={reviewData?.minimalFreight} />
    case 'itemsWithDiscount':
      return <ItemsDiscountReason reason={reviewData?.itemsWithDiscount} />
    case 'dirtRoadQuiz':
      return <DirtRoadReason reason={reviewData?.dirtRoadQuiz} />
    default:
      return null
  }
}

const hasReviewReason = (
  key: ReviewReasonKey,
  reviewData?: OrderReviewReasonsData
) => {
  const value = reviewData?.[key]
  if (key === 'itemsWithDiscount') {
    return Array.isArray(value) && value.length > 0
  }
  return !!value
}

const OrderProductsList = ({
  title,
  products,
  isLoading,
  nameColumnLabel = 'Produto'
}: {
  title: string
  products?: OrderLineItem[]
  isLoading?: boolean
  nameColumnLabel?: string
}) => (
  <Grid item xs={12} md={6}>
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
      {!isLoading && products !== undefined && (
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 0.75, fontWeight: 400 }}
        >
          ({products.length})
        </Typography>
      )}
    </Typography>
    {isLoading && (
      <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
    )}
    {!isLoading && !products?.length && (
      <Typography variant="body2" color="text.secondary">
        Nenhum item encontrado.
      </Typography>
    )}
    {!isLoading && !!products?.length && (
      <TableContainer
        sx={{
          maxHeight: 300,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadCellSx} width={64}>
                Cód.
              </TableCell>
              <TableCell sx={tableHeadCellSx}>{nameColumnLabel}</TableCell>
              <TableCell sx={tableHeadCellSx} align="right" width={52}>
                Qtd
              </TableCell>
              <TableCell sx={tableHeadCellSx} align="right" width={96}>
                Valor
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((item: OrderLineItem, index: number) => {
              const productId = item.productId || item.product?.id || '-'
              const productName = item.name || item.product?.name || '-'
              const config = item?.configurationDescription

              return (
                <TableRow
                  key={`${productId}-${index}`}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell sx={tableCellSx}>{productId}</TableCell>
                  <TableCell sx={{ ...tableCellSx, maxWidth: 0 }}>
                    <Tooltip title={productName}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {productName}
                      </Typography>
                    </Tooltip>
                    {config && (
                      <Tooltip title={config}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                          sx={{ mt: 0.25 }}
                        >
                          {config}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={tableCellSx} align="right">
                    <Typography variant="body2">{item.quantity}</Typography>
                  </TableCell>
                  <TableCell sx={tableCellSx} align="right">
                    <Typography variant="body2" noWrap>
                      {formatCurrency(item.salePrice)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Grid>
)

export const ApproveReserveOrderLinkModal = () => {
  const setModalIsOpen =
    useApproveReserveOrderLinkModalStore.useSetModalIsOpen()
  const isModalOpen = useApproveReserveOrderLinkModalStore.useIsModalOpen()
  const proposalId = useApproveReserveOrderLinkModalStore.useProposalId()

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const { data, isLoading: motivesLoading } = useLoadOrderReviewReasons({
    proposalId
  })

  const { data: currentProposal, isLoading: currentProposalLoading } =
    useLoadOrderProposal(proposalId || undefined)

  const cdPedidoReserva =
    data?.data?.proposal?.cdPedidoReserva?.trim() ||
    currentProposal?.cdPedidoReserva?.trim()

  const { data: reserveOrder, isLoading: reserveLoading } = useLoadOrder(
    cdPedidoReserva || undefined
  )

  const [isRejectOpen, setIsRejectOpen] = useState(false)

  const { mutate: approve, isLoading: isApproving } =
    useApproveReserveOrderLink({
      onSuccess: () => setModalIsOpen(false)
    })

  const handleApprove = () => {
    approve(proposalId)
  }

  const reviewData = data?.data

  const hasMotives = reviewReasonKeys.some((key) =>
    hasReviewReason(key, reviewData)
  )

  return (
    <Dialog
      fullScreen={fullScreen}
      scroll="paper"
      open={isModalOpen}
      onClose={() => setModalIsOpen(false)}
      keepMounted={false}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: fullScreen ? undefined : '90vh'
        }
      }}
    >
      <CardHeader
        title={`Aprovar Reserva — Orçamento n° ${proposalId}`}
        HeaderRight={
          <IconButton onClick={() => setModalIsOpen(false)}>
            <Close />
          </IconButton>
        }
      />
      <Divider />

      <DialogContent sx={{ py: 1.5, px: 2 }}>
        <Grid container spacing={1.5}>
          {(motivesLoading || currentProposalLoading) && (
            <Grid item xs={12}>
              <Loading />
            </Grid>
          )}

          {!motivesLoading && !currentProposalLoading && cdPedidoReserva && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Pedido reserva vinculado (nº pedido):{' '}
                <strong>{cdPedidoReserva}</strong>
              </Typography>
            </Grid>
          )}

          {!motivesLoading && !currentProposalLoading && hasMotives && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Motivos de aprovação
              </Typography>
            </Grid>
          )}

          {!motivesLoading &&
            !currentProposalLoading &&
            reviewReasonKeys.map((reasonKey) => (
              <ReviewReasonBlock
                key={reasonKey}
                reasonKey={reasonKey}
                reviewData={reviewData}
              />
            ))}

          {!motivesLoading && !currentProposalLoading && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <OrderProductsList
                title={`Pedido reserva — nº ${cdPedidoReserva || '-'}`}
                products={reserveOrder?.products}
                isLoading={reserveLoading && !!cdPedidoReserva}
                nameColumnLabel="Material"
              />
              <OrderProductsList
                title={`Orçamento atual — nº ${proposalId}`}
                products={
                  data?.data?.proposal?.products || currentProposal?.products
                }
                nameColumnLabel="Produto"
              />
            </>
          )}
        </Grid>
      </DialogContent>
      <Divider />

      <DialogActions sx={{ justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
        <Button variant="text" onClick={() => setModalIsOpen(false)}>
          Fechar
        </Button>
        <Button
          variant="outlined"
          color="error"
          type="button"
          onClick={() => setIsRejectOpen(true)}
          disabled={isApproving}
        >
          Rejeitar
        </Button>
        <Button type="button" onClick={handleApprove} disabled={isApproving}>
          Aprovar
        </Button>
      </DialogActions>

      <RejectReserveOrderLinkModal
        proposalId={proposalId}
        open={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        onRejected={() => {
          setIsRejectOpen(false)
          setModalIsOpen(false)
        }}
      />
    </Dialog>
  )
}
